export type ScriptRef = {
  id: string;
  href: string;
};

const HTTP_PATTERN = /^https?:\/\//i;

/**
 * True when href is an absolute URL outside the guest app's hostUrl origin.
 * @param {string} href - Resolved script URL.
 * @param {string} hostUrl - Guest application base URL.
 * @returns {boolean} Whether the script is hosted outside the guest app.
 */
export const isExternalScriptHref = (
  href: string,
  hostUrl: string,
): boolean => {
  if (!HTTP_PATTERN.test(href)) return false;

  try {
    return !href.startsWith(new URL(hostUrl).href);
  } catch {
    return true;
  }
};

/**
 * Splits scripts into external (CDN) and local (guest host) groups while
 * preserving manifest order within each group.
 * @param {ScriptRef[]} scripts - Ordered script entries to partition.
 * @param {string} hostUrl - Guest application base URL.
 * @returns {{ external: ScriptRef[]; local: ScriptRef[] }} Partitioned script groups.
 */
export const partitionScriptsByOrigin = (
  scripts: ScriptRef[],
  hostUrl: string,
): { external: ScriptRef[]; local: ScriptRef[] } => {
  const external: ScriptRef[] = [];
  const local: ScriptRef[] = [];

  scripts.forEach((script) => {
    if (isExternalScriptHref(script.href, hostUrl)) {
      external.push(script);
    } else {
      local.push(script);
    }
  });

  return { external, local };
};
